import { useState } from 'react';
import {
  DollarSign, AlertTriangle, CheckCircle2, XCircle, ArrowRightLeft,
  Calculator, TrendingUp, Briefcase, Users, Info, Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/api/client';

// ==================== Helpers ====================

const fmtAUD = (v: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);

const capColor = (pct: number) =>
  pct > 95 ? 'bg-red-500' : pct > 80 ? 'bg-amber-500' : 'bg-green-500';

const capTextColor = (pct: number) =>
  pct > 95 ? 'text-red-600' : pct > 80 ? 'text-amber-600' : 'text-green-600';

// ==================== Mock Data ====================

interface Member {
  id: string;
  name: string;
  fund: string;
  age: number;
  tsb: number; // Total Super Balance at 30 June prior
  employmentStatus: 'full-time' | 'part-time' | 'self-employed' | 'retired';
  hoursWorked30Days: number;
  income: number;
  concessionalYTD: number;
  nonConcessionalYTD: number;
  salarySacrifice: number;
  employerSG: number;
  personalDeductible: number;
  nonConcessional: number;
  downsizer: number;
  spouse: number;
  coContribution: number;
  carryForwardHistory: { fy: string; unused: number }[];
}

const MEMBERS: Member[] = [
  {
    id: 'm1',
    name: 'James Smith',
    fund: 'Smith Family Super Fund',
    age: 52,
    tsb: 800_000,
    employmentStatus: 'full-time',
    hoursWorked30Days: 160,
    income: 320_000,
    concessionalYTD: 22_500,
    nonConcessionalYTD: 40_000,
    salarySacrifice: 10_000,
    employerSG: 12_500,
    personalDeductible: 0,
    nonConcessional: 40_000,
    downsizer: 0,
    spouse: 0,
    coContribution: 0,
    carryForwardHistory: [
      { fy: '2021-22', unused: 5_000 },
      { fy: '2022-23', unused: 8_500 },
      { fy: '2023-24', unused: 3_000 },
      { fy: '2024-25', unused: 12_000 },
      { fy: '2025-26', unused: 7_500 },
    ],
  },
  {
    id: 'm2',
    name: 'Sarah Smith',
    fund: 'Smith Family Super Fund',
    age: 49,
    tsb: 420_000,
    employmentStatus: 'part-time',
    hoursWorked30Days: 60,
    income: 85_000,
    concessionalYTD: 28_500,
    nonConcessionalYTD: 110_000,
    salarySacrifice: 18_000,
    employerSG: 7_500,
    personalDeductible: 3_000,
    nonConcessional: 110_000,
    downsizer: 0,
    spouse: 0,
    coContribution: 500,
    carryForwardHistory: [
      { fy: '2021-22', unused: 0 },
      { fy: '2022-23', unused: 2_000 },
      { fy: '2023-24', unused: 0 },
      { fy: '2024-25', unused: 4_500 },
      { fy: '2025-26', unused: 1_500 },
    ],
  },
  {
    id: 'm3',
    name: 'Robert Johnson',
    fund: 'Johnson Retirement Fund',
    age: 70,
    tsb: 1_950_000,
    employmentStatus: 'retired',
    hoursWorked30Days: 0,
    income: 45_000,
    concessionalYTD: 5_000,
    nonConcessionalYTD: 0,
    salarySacrifice: 0,
    employerSG: 0,
    personalDeductible: 5_000,
    nonConcessional: 0,
    downsizer: 0,
    spouse: 0,
    coContribution: 0,
    carryForwardHistory: [
      { fy: '2021-22', unused: 25_000 },
      { fy: '2022-23', unused: 20_000 },
      { fy: '2023-24', unused: 27_500 },
      { fy: '2024-25', unused: 30_000 },
      { fy: '2025-26', unused: 25_000 },
    ],
  },
];

const CONCESSIONAL_CAP = 30_000;
const NON_CONCESSIONAL_CAP = 120_000;
const DIV293_THRESHOLD = 250_000;

interface Rollover {
  date: string;
  type: 'in' | 'out';
  fromFund: string;
  toFund: string;
  amount: number;
  superStreamRef: string;
  status: 'completed' | 'pending' | 'rejected';
}

const ROLLOVERS: Rollover[] = [
  { date: '15/03/2026', type: 'in', fromFund: 'AustralianSuper', toFund: 'Smith Family Super Fund', amount: 85_000, superStreamRef: 'SSR-2026-001482', status: 'completed' },
  { date: '22/02/2026', type: 'out', fromFund: 'Johnson Retirement Fund', toFund: 'REST Super', amount: 120_000, superStreamRef: 'SSR-2026-001203', status: 'completed' },
  { date: '10/04/2026', type: 'in', fromFund: 'Hostplus', toFund: 'Johnson Retirement Fund', amount: 45_000, superStreamRef: 'SSR-2026-001891', status: 'pending' },
  { date: '01/04/2026', type: 'out', fromFund: 'Smith Family Super Fund', toFund: 'Sunsuper', amount: 30_000, superStreamRef: 'SSR-2026-001750', status: 'rejected' },
];

const ROLLOVER_BADGE: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  completed: { label: 'Completed', variant: 'default' },
  pending: { label: 'Pending', variant: 'secondary' },
  rejected: { label: 'Rejected', variant: 'destructive' },
};

// ==================== Component ====================

export default function SMSFContributionsPage() {
  const [selectedFundId, setSelectedFundId] = useState<string>('');
  const [selectedMemberId, setSelectedMemberId] = useState(MEMBERS[0].id);

  // Fetch funds list
  const { data: fundsData, isLoading: fundsLoading } = useQuery({
    queryKey: ['smsf-funds'],
    queryFn: async () => { const r = await apiClient.get('/smsf/funds'); return r.data.data; },
    staleTime: 5 * 60 * 1000,
  });

  const fundId = selectedFundId || fundsData?.[0]?.id || '';

  // Fetch contributions for selected fund
  const { data: contributionsData, isLoading: contributionsLoading } = useQuery({
    queryKey: ['smsf-contributions', fundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${fundId}/contributions`); return r.data.data; },
    staleTime: 5 * 60 * 1000,
    enabled: !!fundId,
  });

  // Fetch members for selected fund
  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ['smsf-members', fundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${fundId}/members`); return r.data.data; },
    staleTime: 5 * 60 * 1000,
    enabled: !!fundId,
  });

  // Fetch rollovers for selected fund
  const { data: rolloversData, isLoading: rolloversLoading } = useQuery({
    queryKey: ['smsf-rollovers', fundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${fundId}/rollovers`); return r.data.data; },
    staleTime: 5 * 60 * 1000,
    enabled: !!fundId,
  });

  // Use API data with mock fallback
  const members: Member[] = membersData || MEMBERS;
  const rollovers: Rollover[] = rolloversData || ROLLOVERS;

  const selectedMember = members.find(m => m.id === selectedMemberId) || members[0];
  const isLoading = fundsLoading || contributionsLoading || membersLoading;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={DollarSign}
        title="Contributions & Caps Management"
        description="Track contribution caps, carry-forward, bring-forward and compliance"
      />

      {/* Fund Selector */}
      {fundsData && fundsData.length > 0 && (
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-muted-foreground">Fund:</label>
          <Select value={fundId} onValueChange={setSelectedFundId}>
            <SelectTrigger className="w-[320px]"><SelectValue placeholder="Select a fund..." /></SelectTrigger>
            <SelectContent>
              {fundsData.map((f: any) => (
                <SelectItem key={f.id} value={f.id}>{f.fund_name || f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
      )}

      <Tabs defaultValue="cap-tracker" className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="cap-tracker">Cap Tracker</TabsTrigger>
          <TabsTrigger value="bring-forward">Bring-Forward</TabsTrigger>
          <TabsTrigger value="carry-forward">Carry-Forward</TabsTrigger>
          <TabsTrigger value="work-test">Work Test</TabsTrigger>
          <TabsTrigger value="allocations">Allocations</TabsTrigger>
          <TabsTrigger value="div293">Div 293</TabsTrigger>
          <TabsTrigger value="rollovers">Rollovers</TabsTrigger>
        </TabsList>

        {/* ===== Cap Tracker ===== */}
        <TabsContent value="cap-tracker">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Contribution Cap Usage - FY 2025-26</CardTitle>
              <CardDescription>Monitor concessional ($30K) and non-concessional ($120K) caps per member</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Member</th>
                      <th className="pb-2 pr-4 font-medium">Fund</th>
                      <th className="pb-2 pr-6 font-medium">Concessional</th>
                      <th className="pb-2 pr-6 font-medium">Non-Concessional</th>
                      <th className="pb-2 pr-4 font-medium text-right">Unused Carry-Forward</th>
                      <th className="pb-2 font-medium text-right">TSB at 30 June</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map(m => {
                      const ccPct = (m.concessionalYTD / CONCESSIONAL_CAP) * 100;
                      const ncPct = (m.nonConcessionalYTD / NON_CONCESSIONAL_CAP) * 100;
                      const totalCarryFwd = m.tsb < 500_000
                        ? m.carryForwardHistory.reduce((s, h) => s + h.unused, 0)
                        : 0;
                      return (
                        <tr key={m.id} className="border-b last:border-0">
                          <td className="py-3 pr-4 font-medium">{m.name}</td>
                          <td className="py-3 pr-4 text-muted-foreground text-xs">{m.fund}</td>
                          <td className="py-3 pr-6">
                            <div className="space-y-1">
                              <div className="flex justify-between text-xs">
                                <span>{fmtAUD(m.concessionalYTD)} / {fmtAUD(CONCESSIONAL_CAP)}</span>
                                <span className={cn('font-medium', capTextColor(ccPct))}>{ccPct.toFixed(0)}%</span>
                              </div>
                              <div className="h-2 w-full rounded-full bg-secondary overflow-hidden">
                                <div className={cn('h-full rounded-full transition-all', capColor(ccPct))} style={{ width: `${Math.min(ccPct, 100)}%` }} />
                              </div>
                            </div>
                          </td>
                          <td className="py-3 pr-6">
                            <div className="space-y-1">
                              <div className="flex justify-between text-xs">
                                <span>{fmtAUD(m.nonConcessionalYTD)} / {fmtAUD(NON_CONCESSIONAL_CAP)}</span>
                                <span className={cn('font-medium', capTextColor(ncPct))}>{ncPct.toFixed(0)}%</span>
                              </div>
                              <div className="h-2 w-full rounded-full bg-secondary overflow-hidden">
                                <div className={cn('h-full rounded-full transition-all', capColor(ncPct))} style={{ width: `${Math.min(ncPct, 100)}%` }} />
                              </div>
                            </div>
                          </td>
                          <td className="py-3 pr-4 text-right font-medium">{totalCarryFwd > 0 ? fmtAUD(totalCarryFwd) : '-'}</td>
                          <td className="py-3 text-right">{fmtAUD(m.tsb)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Bring-Forward ===== */}
        <TabsContent value="bring-forward">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Calculator className="h-4 w-4" /> Bring-Forward Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Select Member</label>
                  <Select value={selectedMemberId} onValueChange={setSelectedMemberId}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {members.map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Total Super Balance (TSB)</span><span className="font-medium">{fmtAUD(selectedMember.tsb)}</span></div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Eligible for Bring-Forward?</span>
                    {selectedMember.tsb < 1_900_000
                      ? <Badge variant="default">Yes</Badge>
                      : <Badge variant="destructive">No - TSB exceeds $1.9M</Badge>
                    }
                  </div>
                  {selectedMember.tsb < 1_900_000 && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Bring-Forward Period</span>
                        <span className="font-medium">
                          {selectedMember.tsb < 1_660_000 ? '3 years' : selectedMember.tsb < 1_780_000 ? '2 years' : '1 year (no bring-forward)'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Maximum NCC</span>
                        <span className="font-bold text-lg">
                          {fmtAUD(
                            selectedMember.tsb < 1_660_000 ? 360_000
                              : selectedMember.tsb < 1_780_000 ? 240_000
                              : 120_000
                          )}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Bring-Forward Scenarios</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="pb-2 pr-4 font-medium">TSB Range</th>
                        <th className="pb-2 pr-4 font-medium text-center">Period</th>
                        <th className="pb-2 font-medium text-right">Max NCC</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm">
                      <tr className="border-b"><td className="py-2 pr-4">Below $1.66M</td><td className="py-2 pr-4 text-center">3 years</td><td className="py-2 text-right font-medium">{fmtAUD(360_000)}</td></tr>
                      <tr className="border-b"><td className="py-2 pr-4">$1.66M - $1.78M</td><td className="py-2 pr-4 text-center">2 years</td><td className="py-2 text-right font-medium">{fmtAUD(240_000)}</td></tr>
                      <tr className="border-b"><td className="py-2 pr-4">$1.78M - $1.9M</td><td className="py-2 pr-4 text-center">1 year</td><td className="py-2 text-right font-medium">{fmtAUD(120_000)}</td></tr>
                      <tr><td className="py-2 pr-4">$1.9M or above</td><td className="py-2 pr-4 text-center">Nil</td><td className="py-2 text-right font-medium">{fmtAUD(0)}</td></tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ===== Carry-Forward ===== */}
        <TabsContent value="carry-forward">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Unused Concessional Cap Carry-Forward (5-Year History)</CardTitle>
              <CardDescription>Available only if TSB is below $500,000 at 30 June of the prior year</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Member</th>
                      <th className="pb-2 pr-4 font-medium">Eligibility</th>
                      {['2021-22', '2022-23', '2023-24', '2024-25', '2025-26'].map(fy => (
                        <th key={fy} className="pb-2 pr-4 font-medium text-right">{fy}</th>
                      ))}
                      <th className="pb-2 font-medium text-right">Total Available</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map(m => {
                      const eligible = m.tsb < 500_000;
                      const total = eligible ? m.carryForwardHistory.reduce((s, h) => s + h.unused, 0) : 0;
                      return (
                        <tr key={m.id} className="border-b last:border-0">
                          <td className="py-3 pr-4 font-medium">{m.name}</td>
                          <td className="py-3 pr-4">
                            <Badge variant={eligible ? 'default' : 'outline'}>{eligible ? 'Eligible' : 'Not Eligible'}</Badge>
                          </td>
                          {m.carryForwardHistory.map(h => (
                            <td key={h.fy} className="py-3 pr-4 text-right">
                              {eligible ? fmtAUD(h.unused) : <span className="text-muted-foreground">-</span>}
                            </td>
                          ))}
                          <td className="py-3 text-right font-bold">{eligible ? fmtAUD(total) : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Work Test ===== */}
        <TabsContent value="work-test">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Work Test Assessment</CardTitle>
              <CardDescription>Members aged 67-74 must satisfy the work test (40 hours in a consecutive 30-day period)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {members.filter(m => m.age >= 67 && m.age <= 74).length > 0 && (
                <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span>Members aged 67-74 require work test verification before accepting contributions.</span>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Member</th>
                      <th className="pb-2 pr-4 font-medium text-center">Age</th>
                      <th className="pb-2 pr-4 font-medium">Employment Status</th>
                      <th className="pb-2 pr-4 font-medium text-center">Hours (30 days)</th>
                      <th className="pb-2 font-medium">Work Test Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map(m => {
                      let status: string;
                      let variant: 'default' | 'destructive' | 'secondary';
                      if (m.age < 67) {
                        status = 'Exempt (under 67)';
                        variant = 'secondary';
                      } else if (m.age > 74) {
                        status = 'Not Met (over 74)';
                        variant = 'destructive';
                      } else if (m.hoursWorked30Days >= 40) {
                        status = 'Met';
                        variant = 'default';
                      } else {
                        status = 'Not Met';
                        variant = 'destructive';
                      }
                      return (
                        <tr key={m.id} className="border-b last:border-0">
                          <td className="py-3 pr-4 font-medium">{m.name}</td>
                          <td className="py-3 pr-4 text-center">{m.age}</td>
                          <td className="py-3 pr-4 capitalize">{m.employmentStatus.replace('-', ' ')}</td>
                          <td className="py-3 pr-4 text-center">{m.hoursWorked30Days}</td>
                          <td className="py-3"><Badge variant={variant}>{status}</Badge></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Allocations ===== */}
        <TabsContent value="allocations">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Contribution Allocations by Type</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Member</th>
                      <th className="pb-2 pr-4 font-medium text-right">Salary Sacrifice</th>
                      <th className="pb-2 pr-4 font-medium text-right">Employer SG</th>
                      <th className="pb-2 pr-4 font-medium text-right">Personal Ded.</th>
                      <th className="pb-2 pr-4 font-medium text-right">Non-Concessional</th>
                      <th className="pb-2 pr-4 font-medium text-right">Downsizer</th>
                      <th className="pb-2 pr-4 font-medium text-right">Spouse</th>
                      <th className="pb-2 pr-4 font-medium text-right">Co-Contribution</th>
                      <th className="pb-2 font-medium text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map(m => {
                      const total = m.salarySacrifice + m.employerSG + m.personalDeductible + m.nonConcessional + m.downsizer + m.spouse + m.coContribution;
                      return (
                        <tr key={m.id} className="border-b last:border-0">
                          <td className="py-3 pr-4 font-medium">{m.name}</td>
                          <td className="py-3 pr-4 text-right">{fmtAUD(m.salarySacrifice)}</td>
                          <td className="py-3 pr-4 text-right">{fmtAUD(m.employerSG)}</td>
                          <td className="py-3 pr-4 text-right">{fmtAUD(m.personalDeductible)}</td>
                          <td className="py-3 pr-4 text-right">{fmtAUD(m.nonConcessional)}</td>
                          <td className="py-3 pr-4 text-right">{m.downsizer > 0 ? fmtAUD(m.downsizer) : '-'}</td>
                          <td className="py-3 pr-4 text-right">{m.spouse > 0 ? fmtAUD(m.spouse) : '-'}</td>
                          <td className="py-3 pr-4 text-right">{m.coContribution > 0 ? fmtAUD(m.coContribution) : '-'}</td>
                          <td className="py-3 text-right font-bold">{fmtAUD(total)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={members.map(m => ({
                    name: m.name.split(' ')[0],
                    'Salary Sacrifice': m.salarySacrifice,
                    'Employer SG': m.employerSG,
                    'Personal Ded.': m.personalDeductible,
                    'Non-Concessional': m.nonConcessional,
                    'Co-Contribution': m.coContribution,
                  }))}>
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={v => `$${(v / 1000).toFixed(0)}K`} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v: number) => fmtAUD(v)} cursor={false} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Salary Sacrifice" stackId="a" fill="#5048e5" activeBar={false}/>
                    <Bar dataKey="Employer SG" stackId="a" fill="#10b981" activeBar={false}/>
                    <Bar dataKey="Personal Ded." stackId="a" fill="#f59e0b" activeBar={false}/>
                    <Bar dataKey="Non-Concessional" stackId="a" fill="#3b82f6" activeBar={false}/>
                    <Bar dataKey="Co-Contribution" stackId="a" fill="#8b5cf6" activeBar={false}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Div 293 ===== */}
        <TabsContent value="div293">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Division 293 Tax Calculator</CardTitle>
              <CardDescription>Additional 15% tax on concessional contributions for income + contributions exceeding {fmtAUD(DIV293_THRESHOLD)}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Member</th>
                      <th className="pb-2 pr-4 font-medium text-right">Income</th>
                      <th className="pb-2 pr-4 font-medium text-right">Concessional Contributions</th>
                      <th className="pb-2 pr-4 font-medium text-right">Combined Total</th>
                      <th className="pb-2 pr-4 font-medium text-right">Excess over {fmtAUD(DIV293_THRESHOLD)}</th>
                      <th className="pb-2 pr-4 font-medium text-right">Div 293 Tax (15%)</th>
                      <th className="pb-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map(m => {
                      const combined = m.income + m.concessionalYTD;
                      const excess = Math.max(0, combined - DIV293_THRESHOLD);
                      const taxableAmount = Math.min(excess, m.concessionalYTD);
                      const div293Tax = taxableAmount * 0.15;
                      return (
                        <tr key={m.id} className="border-b last:border-0">
                          <td className="py-3 pr-4 font-medium">{m.name}</td>
                          <td className="py-3 pr-4 text-right">{fmtAUD(m.income)}</td>
                          <td className="py-3 pr-4 text-right">{fmtAUD(m.concessionalYTD)}</td>
                          <td className="py-3 pr-4 text-right font-medium">{fmtAUD(combined)}</td>
                          <td className="py-3 pr-4 text-right">{excess > 0 ? fmtAUD(excess) : '-'}</td>
                          <td className="py-3 pr-4 text-right font-bold text-red-600">{div293Tax > 0 ? fmtAUD(div293Tax) : '-'}</td>
                          <td className="py-3">
                            <Badge variant={div293Tax > 0 ? 'destructive' : 'default'}>
                              {div293Tax > 0 ? 'Liable' : 'Not Liable'}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Rollovers ===== */}
        <TabsContent value="rollovers">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Rollover Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Date</th>
                      <th className="pb-2 pr-4 font-medium">Type</th>
                      <th className="pb-2 pr-4 font-medium">From</th>
                      <th className="pb-2 pr-4 font-medium">To</th>
                      <th className="pb-2 pr-4 font-medium text-right">Amount</th>
                      <th className="pb-2 pr-4 font-medium">SuperStream Ref</th>
                      <th className="pb-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rollovers.map((r, i) => {
                      const badge = ROLLOVER_BADGE[r.status];
                      return (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-3 pr-4 text-xs">{r.date}</td>
                          <td className="py-3 pr-4">
                            <Badge variant="outline" className="gap-1">
                              <ArrowRightLeft className="h-3 w-3" />
                              {r.type === 'in' ? 'Roll-In' : 'Roll-Out'}
                            </Badge>
                          </td>
                          <td className="py-3 pr-4">{r.fromFund}</td>
                          <td className="py-3 pr-4">{r.toFund}</td>
                          <td className="py-3 pr-4 text-right font-medium">{fmtAUD(r.amount)}</td>
                          <td className="py-3 pr-4 font-mono text-xs text-muted-foreground">{r.superStreamRef}</td>
                          <td className="py-3"><Badge variant={badge.variant}>{badge.label}</Badge></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
